/**
 * DynamicFormField — renders a single form field based on its type.
 * Mirrors Flutter BuildFormField (lib/presentation/common/form_util.dart).
 *
 * Types: TEXT_FLD (+ currency / mobile prefixes), DESCRIPTION_TEXT_FLD, SEARCH_TEXT_FLD,
 *        SEARCH_DROP_DOWN (searchable, options loaded like Flutter's overlay dropdown),
 *        DROPDOWN_FOR_SINGLE_SELECTION (CustomDropdownButtonFormField), EXPERIENCE_DROP_DOWN
 *        (years + months), DATE (last 100 years, no future dates), RADIO_BUTTON, CHECKBOX,
 *        EMPTY (spacer). Any other type renders nothing, like BuildFormField's default branch.
 */
import { useState, useCallback, useRef, useId, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldType } from '@/types/application.types';
import type { FldListState, OptionData } from '@/types/application.types';
import { NAME_DISALLOWED_CHARACTERS } from '@/utils/formValidators';
import { validateFormField } from '../utils/formHelpers';
import type { ValidationContext } from '../utils/formHelpers';
import { getOptionSource } from '../utils/fieldOptions';
import { CountryCodeSelect } from '@/features/auth/components/CountryCodeSelect';
import { SearchSelect } from './SearchSelect';
import styles from './DynamicFormField.module.css';

interface DynamicFormFieldProps {
  field: FldListState;
  /** `displayValue` is the option label for dropdown selections (Flutter field.displayValue). */
  onChange: (value: string, displayValue?: string) => void;
  error?: string;
  disabled?: boolean;
  /** Replaces the default blur validation — Flutter BuildFormField's per-form `validator`. */
  validator?: (field: FldListState) => string | null;
  /** Context for the default validation (start/end dates of the same entry). */
  validationContext?: ValidationContext;
  /** EXPERIENCE_DROP_DOWN / EXP pair: the months field that follows it (Flutter listOfFlds[topIndex + 1]). */
  monthsField?: FldListState;
  onMonthsChange?: (value: string) => void;
  /** Mobile fields: shows the country code picker (Flutter showCountryCodeBottomSheet). */
  onCountryCodeChange?: (phoneCode: string) => void;
  /** Currency fields: makes the currency prefix selectable (Flutter showCurrencyCodes). */
  onCurrencyChange?: (currencyCode: string) => void;
  /** SEARCH_TEXT_FLD: suggestions (Flutter suggestionsCallback). Without it the field is plain text. */
  fetchSuggestions?: (query: string) => Promise<OptionData[]>;
  /** Extra content under the input (e.g. "Still working for this company"). */
  footer?: ReactNode;
}

/**
 * Flutter BuildFormField TEXT_FLD inputFormatters: digitsOnly → digits,
 * mobile → digits/space/hyphen, first/last name → name characters. Flutter keeps only [a-zA-Z]
 * in a name; here a name keeps the characters the sign-up form accepts (formValidators
 * NAME_DISALLOWED_CHARACTERS: any script, spaces, apostrophes, hyphens, periods), so "José" or
 * "O'Brien" saved at sign-up can be edited without being cut down.
 */
function filterTextInput(field: FldListState, value: string): string {
  if (field.digitsOnly) return value.replace(/[^0-9]/g, '');
  if (field.isMobileFld) return value.replace(/[^0-9\s-]/g, '');
  if (field.key === 'firstName' || field.key === 'lastName') {
    return value.replace(NAME_DISALLOWED_CHARACTERS, '');
  }
  return value;
}

const FALLBACK_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD', 'JPY', 'CNY'];

function currencyCodes(): string[] {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    return intl.supportedValuesOf?.('currency') ?? FALLBACK_CURRENCIES;
  } catch {
    return FALLBACK_CURRENCIES;
  }
}

/**
 * Flutter CustomDropdownButtonFormField (form_util.dart): each option is relabelled "N year(s)" when
 * the list has more than 40 entries, otherwise "N month(s)", and the choice is stored as the int N.
 * A stored 0 or a value over 40 shows as no selection.
 */
function singleSelection(field: FldListState, t: (key: string) => string) {
  const many = field.dropDownOptions.length > 40;
  const unit = (key: string) => (many ? (key === '1' ? 'year' : 'years') : key === '1' ? 'month' : 'months');
  const options: OptionData[] = field.dropDownOptions.flatMap((opt) => {
    const digits = opt.key.replace(/[^0-9]/g, '');
    return digits ? [{ key: String(parseInt(digits, 10)), displayKey: `${opt.key} ${t(unit(opt.key))}` }] : [];
  });
  const value = String(field.value ?? '');
  const n = /^\d+$/.test(value) ? parseInt(value, 10) : 0;
  const selected = n > 0 && n <= 40 ? String(n) : '';
  if (selected && !options.some((opt) => opt.key === selected)) {
    options.push({ key: selected, displayKey: `${selected} ${t(unit(selected))}` });
  }
  return { options, selected };
}

/** Radio options: `{ label, value }` (questionnaire), `{ key, displayKey }` or plain strings. */
function radioOptions(data: unknown[] | undefined): OptionData[] {
  return (data ?? []).map((item) => {
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const key = String(o.value ?? o.key ?? '');
      return { key, displayKey: String(o.label ?? o.displayKey ?? key) };
    }
    return { key: String(item), displayKey: String(item) };
  });
}

export function DynamicFormField({
  field,
  onChange,
  error,
  disabled,
  validator,
  validationContext,
  monthsField,
  onMonthsChange,
  onCountryCodeChange,
  onCurrencyChange,
  fetchSuggestions,
  footer,
}: DynamicFormFieldProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const isDisabled = disabled || field.isDisabled;
  const [localError, setLocalError] = useState<string | null>(null);
  const [searchOptions, setSearchOptions] = useState<OptionData[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const displayError = error || localError;
  const optionSource = useMemo(() => getOptionSource(field), [field]);

  const runValidation = useCallback(
    (fld: FldListState) => (validator ? validator(fld) : validateFormField(fld, t, validationContext)),
    [validator, validationContext, t],
  );

  const handleBlur = useCallback(() => setLocalError(runValidation(field)), [field, runValidation]);

  const handleChange = useCallback(
    (newValue: string, displayValue?: string) => {
      setLocalError(null);
      onChange(newValue, displayValue);
    },
    [onChange],
  );

  /* ── SEARCH_TEXT_FLD suggestions — only with a suggestionsCallback; the application forms pass none ── */
  const handleSearchInput = useCallback(
    (query: string) => {
      // Typed text is both value and label until a suggestion is picked (key ≠ displayKey)
      handleChange(query, query);
      if (!fetchSuggestions) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!query.trim()) {
        setSearchOptions([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        try {
          setSearchOptions(await fetchSuggestions(query));
          setShowDropdown(true);
        } catch {
          setSearchOptions([]);
        }
      }, 300);
    },
    [fetchSuggestions, handleChange],
  );

  // Flutter localizeDropdownOptionDisplayKeyForExperience: "3 years" → "3 <years>"
  const expLabel = useCallback((opt: OptionData) => {
    const [count, unit] = (opt.displayKey || opt.key).split(' ');
    return unit ? `${count} ${t(unit)}` : count;
  }, [t]);

  const label = (
    <label className={styles.label} htmlFor={inputId}>
      {field.title}
      {field.isMandatory && field.title && <span className={styles.required} aria-hidden="true">*</span>}
    </label>
  );
  // Flutter PrimaryTextField: the sub-label sits on the label line after the `*`, so the input
  // stays level with its row neighbours
  const labelWithSubTitle = field.subTitle ? (
    <label className={styles.label} htmlFor={inputId}>
      {field.title}
      {field.isMandatory && field.title && <span className={styles.required} aria-hidden="true">*</span>}
      <span className={styles.subTitle}>{field.subTitle}</span>
    </label>
  ) : label;
  const errorText = displayError ? (
    <span id={errorId} className={styles.error} role="alert">{displayError}</span>
  ) : null;
  const a11y = {
    id: inputId,
    'aria-invalid': !!displayError || undefined,
    'aria-describedby': displayError ? errorId : undefined,
  };

  /* ── Experience (years + months under one label) ── */
  const isExperiencePair =
    field.type === FieldType.EXPERIENCE_DROP_DOWN || (field.subType === 'EXP' && !!monthsField);
  if (isExperiencePair) {
    return (
      <div className={styles.fieldWrapper}>
        {label}
        <div className={styles.experienceRow}>
          <SearchSelect
            value={field.value}
            options={field.dropDownOptions}
            enableSearch={false}
            placeholder={field.hintText}
            disabled={isDisabled}
            invalid={!!displayError}
            ariaLabel={field.title || t('years')}
            formatLabel={expLabel}
            onSelect={(key) => handleChange(key)}
            onBlur={handleBlur}
          />
          {monthsField && onMonthsChange && (
            <SearchSelect
              value={monthsField.value}
              options={monthsField.dropDownOptions}
              enableSearch={false}
              placeholder={monthsField.hintText}
              disabled={isDisabled || monthsField.isDisabled}
              invalid={!!displayError}
              ariaLabel={monthsField.hintText || t('months')}
              formatLabel={expLabel}
              onSelect={(key) => {
                setLocalError(null);
                onMonthsChange(key);
              }}
            />
          )}
        </div>
        {errorText}
        {footer}
      </div>
    );
  }

  switch (field.type) {
    case FieldType.TEXT_FLD: {
      const isMultiline = field.key === 'roleDescription';
      const hasCountryCode = field.isMobileFld && !!onCountryCodeChange;
      return (
        <div className={styles.fieldWrapper}>
          {labelWithSubTitle}
          <div className={`${styles.inputRow} ${hasCountryCode ? styles.phoneRow : ''}`}>
            {field.isCurrencyDropDown &&
              (onCurrencyChange ? (
                <select
                  className={styles.prefixSelect}
                  value={field.currencyCode}
                  onChange={(e) => onCurrencyChange(e.target.value)}
                  disabled={isDisabled}
                  aria-label={t('Currency')}
                >
                  {!currencyCodes().includes(field.currencyCode) && field.currencyCode && (
                    <option value={field.currencyCode}>{field.currencyCode}</option>
                  )}
                  {currencyCodes().map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              ) : (
                <span className={styles.currencyPrefix}>{field.currencyCode}</span>
              ))}
            {hasCountryCode && (
              <CountryCodeSelect
                value={field.countryCode}
                onChange={onCountryCodeChange}
                disabled={isDisabled}
              />
            )}
            {isMultiline ? (
              <textarea
                className={`${styles.input} ${styles.textarea} ${displayError ? styles.inputError : ''}`}
                placeholder={field.hintText || ''}
                value={field.value}
                rows={5}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                disabled={isDisabled}
                {...a11y}
              />
            ) : (
              <input
                className={`${styles.input} ${displayError ? styles.inputError : ''}`}
                type={field.isEmailFld ? 'email' : field.isMobileFld ? 'tel' : 'text'}
                placeholder={field.hintText || ''}
                value={field.value}
                onChange={(e) => handleChange(filterTextInput(field, e.target.value))}
                onBlur={handleBlur}
                disabled={isDisabled}
                readOnly={field.isDisabled}
                inputMode={field.digitsOnly ? 'numeric' : field.isMobileFld ? 'tel' : undefined}
                {...a11y}
              />
            )}
          </div>
          {errorText}
          {footer}
        </div>
      );
    }

    case FieldType.DESCRIPTION_TEXT_FLD:
      return (
        <div className={styles.fieldWrapper}>
          {labelWithSubTitle}
          <textarea
            className={`${styles.input} ${styles.textarea} ${displayError ? styles.inputError : ''}`}
            placeholder={field.hintText || ''}
            value={field.value}
            rows={5}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            disabled={isDisabled}
            {...a11y}
          />
          {errorText}
          {footer}
        </div>
      );

    case FieldType.SEARCH_TEXT_FLD:
      return (
        <div className={styles.fieldWrapper}>
          {label}
          <div className={styles.searchWrapper}>
            <input
              className={`${styles.input} ${displayError ? styles.inputError : ''}`}
              type="text"
              placeholder={field.hintText || ''}
              // Flutter SearchableTextFldForForm shows displayKey and keeps key as the field value
              value={field.displayValue || field.value}
              autoComplete="off"
              onChange={(e) => handleSearchInput(e.target.value)}
              onBlur={() => { setTimeout(() => setShowDropdown(false), 200); handleBlur(); }}
              onFocus={() => { if (searchOptions.length > 0) setShowDropdown(true); }}
              disabled={isDisabled}
              {...a11y}
            />
            {showDropdown && searchOptions.length > 0 && (
              <ul className={styles.dropdown}>
                {searchOptions.map((opt) => (
                  <li
                    key={opt.key}
                    className={styles.dropdownItem}
                    onMouseDown={() => {
                      handleChange(opt.key, opt.displayKey);
                      setShowDropdown(false);
                    }}
                  >
                    {opt.displayKey || opt.key}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {errorText}
          {footer}
        </div>
      );

    case FieldType.SEARCH_DROP_DOWN:
      return (
        <div className={styles.fieldWrapper}>
          {label}
          <SearchSelect
            value={field.value}
            displayValue={field.displayValue}
            options={field.dropDownOptions}
            source={optionSource}
            enableSearch={field.subType !== 'EXP' && field.enableSearch}
            placeholder={field.hintText || t('Select')}
            disabled={isDisabled}
            invalid={!!displayError}
            ariaLabel={field.title}
            formatLabel={field.subType === 'EXP' ? expLabel : undefined}
            onSelect={handleChange}
            onBlur={handleBlur}
          />
          {errorText}
          {footer}
        </div>
      );

    case FieldType.DROPDOWN_FOR_SINGLE_SELECTION: {
      const { options, selected } = singleSelection(field, t);
      return (
        <div className={styles.fieldWrapper}>
          {label}
          <select
            className={`${styles.select} ${displayError ? styles.inputError : ''}`}
            value={selected}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            disabled={isDisabled}
            {...a11y}
          >
            <option value="" disabled>{field.hintText}</option>
            {options.map((opt, i) => (
              <option key={`${opt.key}-${i}`} value={opt.key}>
                {opt.displayKey}
              </option>
            ))}
          </select>
          {errorText}
          {footer}
        </div>
      );
    }

    case FieldType.DATE:
      return (
        <div className={styles.fieldWrapper}>
          {label}
          <input
            className={`${styles.input} ${displayError ? styles.inputError : ''}`}
            type="date"
            value={dateValueToInput(field.value)}
            min={earliestDateInput()}
            max={todayInput()}
            onChange={(e) => handleChange(inputToDateValue(e.target.value))}
            onBlur={handleBlur}
            disabled={isDisabled}
            {...a11y}
          />
          {errorText}
          {footer}
        </div>
      );

    case FieldType.RADIO_BUTTON:
      return (
        <div className={styles.fieldWrapper} role="radiogroup" aria-labelledby={`${inputId}-label`}>
          <span id={`${inputId}-label`} className={styles.label}>
            {field.title}
            {field.isMandatory && <span className={styles.required} aria-hidden="true">*</span>}
          </span>
          <div className={styles.radioGroup}>
            {radioOptions(field.data).map((opt) => (
              <label
                key={opt.key}
                className={`${styles.radioPill} ${field.value === opt.key ? styles.radioPillActive : ''}`}
              >
                <input
                  type="radio"
                  name={inputId}
                  value={opt.key}
                  checked={field.value === opt.key}
                  onChange={() => handleChange(opt.key, opt.displayKey)}
                  disabled={isDisabled}
                />
                <span>{opt.displayKey}</span>
              </label>
            ))}
          </div>
          {errorText}
          {footer}
        </div>
      );

    case FieldType.CHECKBOX:
      return (
        <div className={styles.fieldWrapper}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={field.value === 'true' || field.value === 'Yes'}
              onChange={(e) => handleChange(e.target.checked ? 'true' : 'false')}
              disabled={isDisabled}
            />
            <span>
              {field.title}
              {field.isMandatory && <span className={styles.required} aria-hidden="true">*</span>}
            </span>
          </label>
          {errorText}
        </div>
      );

    case FieldType.EMPTY:
      return <div className={styles.spacer} aria-hidden="true" />;

    // BuildFormField default: an empty box
    default:
      return null;
  }
}

/* ── Date helpers — epoch millis at local midnight, like Flutter's date picker ── */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function dateValueToInput(value: string): string {
  if (!value) return '';
  const num = Number(value);
  if (isNaN(num) || num <= 0) return '';
  const d = new Date(num);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function inputToDateValue(input: string): string {
  if (!input) return '';
  const [y, m, d] = input.split('-').map(Number);
  return String(new Date(y, m - 1, d).getTime());
}

/** Flutter openDatePicker: lastDate = today */
function todayInput(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Flutter openDatePicker: firstDate = DateTime(now.year - 100, 12), i.e. 1 December, 100 years ago */
function earliestDateInput(): string {
  return `${new Date().getFullYear() - 100}-12-01`;
}
